import { Charity } from '../models/charity/charity.model';

/**
 * Mock charity data for testing and development
 * Contains complete team structure: 1 Leader, 5 Executive Team, 5 Board Members
 * Includes gallery images and videos for testing the Gallery tab
 */
export const MOCK_CHARITY_DATA: Charity = {
  id: 'mock-charity-001',
  uuid: 'mock-charity-001',
  name: 'Community Hope and Development Foundation',
  tagline: 'Building a better tomorrow through education and healthcare',
  description: 'Community Hope and Development Foundation is dedicated to improving lives through sustainable development, education, and healthcare initiatives across underserved communities worldwide.',
  championed: true,

  address: {
    street: '123 Charity Lane',
    city: 'Bucharest',
    state: '',
    postalCode: '100001',
    country: 'Romania',
  },

  logo: {
    id: 'logo-001',
    url: 'https://via.placeholder.com/200x200/96080b/ffffff?text=GHF',
    alt: 'Global Hope Foundation Logo',
    type: 'logo',
  },

  badge: {
    id: 'badge-001',
    url: 'https://via.placeholder.com/100x100/FFD700/000000?text=Verified',
    alt: 'Verified Organization Badge',
  },

  images: [
    {
      id: 'img-001',
      url: 'https://via.placeholder.com/800x600/4A90E2/ffffff?text=Community+Center',
      alt: 'Community Center',
      type: 'image',
      isPrimary: true,
      caption: 'Our flagship community center in rural Kenya',
    },
    {
      id: 'img-002',
      url: 'https://via.placeholder.com/800x600/7ED321/ffffff?text=Education+Program',
      alt: 'Education Program',
      type: 'image',
      isPrimary: false,
      caption: 'Children participating in our literacy program',
    },
    {
      id: 'img-003',
      url: 'https://via.placeholder.com/800x600/F5A623/ffffff?text=Healthcare+Clinic',
      alt: 'Healthcare Clinic',
      type: 'image',
      isPrimary: false,
      caption: 'Mobile healthcare clinic serving remote villages',
    },
    {
      id: 'img-004',
      url: 'https://via.placeholder.com/800x600/BD10E0/ffffff?text=Water+Project',
      alt: 'Water Project',
      type: 'image',
      isPrimary: false,
      caption: 'Clean water initiative bringing safe drinking water',
    },
    {
      id: 'img-005',
      url: 'https://via.placeholder.com/800x600/50E3C2/ffffff?text=Agriculture+Training',
      alt: 'Agriculture Training',
      type: 'image',
      isPrimary: false,
      caption: 'Sustainable agriculture training program',
    },
    {
      id: 'img-006',
      url: 'https://via.placeholder.com/800x600/B8E986/ffffff?text=Women+Empowerment',
      alt: 'Women Empowerment',
      type: 'image',
      isPrimary: false,
      caption: 'Women empowerment workshop',
    },
  ],

  videos: [
    {
      id: 'video-001',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      alt: 'Organization Overview Video',
      type: 'video',
      thumbnail: 'https://via.placeholder.com/800x600/E91E63/ffffff?text=Video+1',
      duration: 180,
      caption: 'Learn about our mission and impact',
    },
    {
      id: 'video-002',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      alt: 'Project Impact Story',
      type: 'video',
      thumbnail: 'https://via.placeholder.com/800x600/9C27B0/ffffff?text=Video+2',
      duration: 240,
      caption: 'Stories from the communities we serve',
    },
    {
      id: 'video-003',
      url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
      alt: 'Annual Report Highlights',
      type: 'video',
      thumbnail: 'https://via.placeholder.com/800x600/3F51B5/ffffff?text=Video+3',
      duration: 300,
      caption: '2024 Annual report and achievements',
    },
  ],

  languages: [
    {
      code: 'eng',
      name: 'English',
      isDefault: true,
    },
    {
      code: 'spa',
      name: 'Spanish',
      isDefault: false,
    },
    {
      code: 'fra',
      name: 'French',
      isDefault: false,
    },
  ],

  details: {
    vision: 'A world where every person has access to quality education, healthcare, and opportunities to thrive, regardless of their circumstances or location.',
    mission: 'To empower underserved communities through sustainable development programs that focus on education, healthcare, and economic opportunity, creating lasting positive change.',
    executiveSummary: 'Global Hope Foundation has been working at the forefront of international development for over 15 years. Our comprehensive approach combines education, healthcare, and economic empowerment to create sustainable change in communities across 25 countries. Through partnerships with local organizations and communities, we have impacted over 2 million lives, built 150 schools, established 75 healthcare clinics, and trained 50,000 individuals in sustainable livelihood practices.',
    organisationValues: '• Compassion: We lead with empathy and understanding\n• Integrity: We operate with transparency and accountability\n• Innovation: We embrace creative solutions to complex challenges\n• Collaboration: We believe in the power of partnerships\n• Sustainability: We focus on long-term, lasting impact\n• Respect: We honor the dignity and culture of every community',
    programAndActivities: '• Education Programs: Literacy initiatives, teacher training, school infrastructure development\n• Healthcare Services: Mobile clinics, maternal health programs, disease prevention campaigns\n• Economic Empowerment: Vocational training, microfinance programs, entrepreneurship support\n• Water & Sanitation: Clean water access, sanitation facilities, hygiene education\n• Agriculture Development: Sustainable farming techniques, crop diversification, market access',
    yearFounded: 2009,
    registrationNumber: 'REG-2009-GHF-12345',
    taxId: '12-3456789',
    website: 'www.communityhope.org',
    email: 'info@communityhope.org',
    phone: '+1 (555) 123-4567',
    categories: [
      'Education',
      'Healthcare',
      'Economic Development',
      'Water & Sanitation',
      'Agriculture',
      'Women Empowerment',
      'Child Welfare',
    ],
    influencedCountries: [
      'Kenya',
      'Uganda',
      'Tanzania',
      'Ethiopia',
      'Rwanda',
      'Ghana',
      'Senegal',
      'India',
      'Nepal',
      'Bangladesh',
      'Philippines',
      'Indonesia',
      'Guatemala',
      'Honduras',
      'Peru',
      'Bolivia',
    ],
    socials: {
      facebook: 'https://facebook.com/globalhopefoundation',
      twitter: 'https://twitter.com/globalhopeorg',
      instagram: 'https://instagram.com/globalhopefoundation',
      linkedin: 'https://linkedin.com/company/global-hope-foundation',
    },
    socialMedia: {
      facebook: 'https://facebook.com/globalhopefoundation',
      twitter: 'https://twitter.com/globalhopeorg',
      instagram: 'https://instagram.com/globalhopefoundation',
      linkedin: 'https://linkedin.com/company/global-hope-foundation',
    },
  },

  givingLink: 'https://globalhopefoundation.org/donate',

  // Team: 1 Leader, 5 Executive Team, 5 Board Members
  team: [
    // Organization Leader (1)
    {
      id: 'team-leader-001',
      name: 'Dr. Sarah Johnson',
      role: 'Executive Director & Founder',
      distinction: 'Organization Leader',
      bio: 'Dr. Sarah Johnson is a passionate advocate for global health and education with over 20 years of experience in international development. She founded Global Hope Foundation in 2009 after working with the WHO and UNICEF. Under her leadership, the organization has grown from a small grassroots initiative to a globally recognized nonprofit impacting millions of lives. Sarah holds a PhD in Public Health from Harvard University and an MBA from Stanford.',
      image: {
        id: 'team-img-001',
        url: 'https://via.placeholder.com/400x400/96080b/ffffff?text=SJ',
        alt: 'Dr. Sarah Johnson',
      },
      email: 'sarah.johnson@globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/sarah-johnson',
    },

    // Executive Team (5)
    {
      id: 'team-exec-001',
      name: 'Michael Chen',
      role: 'Chief Operating Officer',
      distinction: 'Executive Team',
      bio: 'Michael brings 15 years of operational excellence in nonprofit management. Previously serving as COO at Save the Children, he specializes in scaling programs while maintaining quality and accountability. His expertise in supply chain management and logistics has been crucial in expanding our reach to remote communities.',
      image: {
        id: 'team-img-002',
        url: 'https://via.placeholder.com/400x400/4A90E2/ffffff?text=MC',
        alt: 'Michael Chen',
      },
      email: 'michael.chen@globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/michael-chen',
    },
    {
      id: 'team-exec-002',
      name: 'Dr. Amara Okafor',
      role: 'Director of Health Programs',
      distinction: 'Executive Team',
      bio: 'Dr. Okafor is a medical doctor and public health expert with extensive experience in sub-Saharan Africa. She oversees all healthcare initiatives and has pioneered innovative community health worker programs that have significantly improved maternal and child health outcomes in our target regions.',
      image: {
        id: 'team-img-003',
        url: 'https://via.placeholder.com/400x400/7ED321/ffffff?text=AO',
        alt: 'Dr. Amara Okafor',
      },
      email: 'amara.okafor@globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/amara-okafor',
    },
    {
      id: 'team-exec-003',
      name: 'James Rodriguez',
      role: 'Director of Education Programs',
      distinction: 'Executive Team',
      bio: 'James has dedicated his career to educational equity and access. With a background in curriculum development and teacher training, he leads our education initiatives across 25 countries. His innovative approach to culturally-responsive education has resulted in a 40% improvement in literacy rates in our program areas.',
      image: {
        id: 'team-img-004',
        url: 'https://via.placeholder.com/400x400/F5A623/ffffff?text=JR',
        alt: 'James Rodriguez',
      },
      email: 'james.rodriguez@globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/james-rodriguez',
    },
    {
      id: 'team-exec-004',
      name: 'Emily Thompson',
      role: 'Chief Development Officer',
      distinction: 'Executive Team',
      bio: 'Emily oversees all fundraising, donor relations, and partnership development. With a track record of securing major grants from foundations and government agencies, she has been instrumental in tripling our annual budget over the past five years. She previously worked at the Bill & Melinda Gates Foundation.',
      image: {
        id: 'team-img-005',
        url: 'https://via.placeholder.com/400x400/BD10E0/ffffff?text=ET',
        alt: 'Emily Thompson',
      },
      email: 'emily.thompson@globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/emily-thompson',
    },
    {
      id: 'team-exec-005',
      name: 'Rajesh Patel',
      role: 'Chief Financial Officer',
      distinction: 'Executive Team',
      bio: 'Rajesh ensures financial sustainability and accountability across all programs. A certified public accountant with international nonprofit experience, he has implemented robust financial systems that have earned us top ratings from Charity Navigator and GuideStar. His commitment to transparency has strengthened donor confidence.',
      image: {
        id: 'team-img-006',
        url: 'https://via.placeholder.com/400x400/50E3C2/ffffff?text=RP',
        alt: 'Rajesh Patel',
      },
      email: 'rajesh.patel@globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/rajesh-patel',
    },

    // Board Members (5)
    {
      id: 'team-board-001',
      name: 'Margaret Williams',
      role: 'Board Chair',
      distinction: 'Board Member',
      bio: 'Margaret is a retired Fortune 500 CEO with a passion for social impact. She brings decades of corporate leadership experience to guide our strategic vision and governance. Her expertise in organizational development has been invaluable in positioning Global Hope Foundation for sustainable growth.',
      image: {
        id: 'team-img-007',
        url: 'https://via.placeholder.com/400x400/B8E986/ffffff?text=MW',
        alt: 'Margaret Williams',
      },
      email: 'margaret.williams@board.globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/margaret-williams',
    },
    {
      id: 'team-board-002',
      name: 'Dr. David Kim',
      role: 'Board Vice Chair',
      distinction: 'Board Member',
      bio: 'Dr. Kim is a professor of international development at Georgetown University and a leading expert on poverty alleviation. His research and academic insights inform our program design and impact measurement strategies, ensuring we remain at the forefront of evidence-based interventions.',
      image: {
        id: 'team-img-008',
        url: 'https://via.placeholder.com/400x400/FF6B6B/ffffff?text=DK',
        alt: 'Dr. David Kim',
      },
      email: 'david.kim@board.globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/david-kim',
    },
    {
      id: 'team-board-003',
      name: 'Linda Martinez',
      role: 'Board Treasurer',
      distinction: 'Board Member',
      bio: 'Linda is a senior partner at a global accounting firm specializing in nonprofit financial management. She chairs our Audit Committee and ensures the highest standards of financial integrity and compliance. Her expertise has been crucial in navigating complex international financial regulations.',
      image: {
        id: 'team-img-009',
        url: 'https://via.placeholder.com/400x400/4ECDC4/ffffff?text=LM',
        alt: 'Linda Martinez',
      },
      email: 'linda.martinez@board.globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/linda-martinez',
    },
    {
      id: 'team-board-004',
      name: 'Ambassador John Foster',
      role: 'Board Secretary',
      distinction: 'Board Member',
      bio: 'Ambassador Foster served as U.S. Ambassador to Kenya and Tanzania and brings invaluable diplomatic experience and international relationships. His deep understanding of African politics and development challenges helps us navigate complex operational environments and build strong local partnerships.',
      image: {
        id: 'team-img-010',
        url: 'https://via.placeholder.com/400x400/95E1D3/ffffff?text=JF',
        alt: 'Ambassador John Foster',
      },
      email: 'john.foster@board.globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/john-foster',
    },
    {
      id: 'team-board-005',
      name: 'Dr. Priya Sharma',
      role: 'Board Member',
      distinction: 'Board Member',
      bio: 'Dr. Sharma is a renowned pediatrician and global health advocate. As founder of a successful social enterprise in India, she brings entrepreneurial thinking and healthcare expertise to our board. Her insights on innovative healthcare delivery models have shaped our approach to community health.',
      image: {
        id: 'team-img-011',
        url: 'https://via.placeholder.com/400x400/F38181/ffffff?text=PS',
        alt: 'Dr. Priya Sharma',
      },
      email: 'priya.sharma@board.globalhopefoundation.org',
      linkedin: 'https://linkedin.com/in/priya-sharma',
    },
  ],

  projects: [
    // Project 1: English + LIVE - UNFUNDED (20% funded)
    {
      id: 'project-001',
      projectUuid: 'project-001',
      name: 'Rural Education Initiative',
      projectName: 'Rural Education Initiative',
      description: 'Building schools and training teachers in remote rural areas to provide quality education access to underserved children.',
      status: 'active',
      startDate: '2023-01-15',
      endDate: '2025-12-31',
      budget: 500000,
      location: {
        city: 'Nairobi Region',
        country: 'Kenya',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/4A90E2/ffffff?text=Rural+Education',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Education',
      projectCategoriesNames: ['Education', 'Infrastructure', 'Teacher Training', 'Community Development', 'Child Welfare', 'Literacy'],
      amountRaisedToDate: 100000,
      totalProjectBudget: 500000,
      daysLeft: 245,
      givingLink: 'https://globalhopefoundation.org/donate/rural-education',
      projectLanguages: [
        { code: 'eng', entityStatus: 'LIVE' },
        { code: 'spa', entityStatus: 'LIVE' },
        { code: 'fra', entityStatus: 'DRAFT' },
      ],
    },
    // Project 2: English + LIVE - UNFUNDED (60% funded)
    {
      id: 'project-002',
      projectUuid: 'project-002',
      name: 'Clean Water Access Program',
      projectName: 'Clean Water Access Program',
      description: 'Installing water wells and purification systems to provide clean drinking water to communities lacking safe water access.',
      status: 'active',
      startDate: '2024-03-01',
      endDate: '2026-02-28',
      budget: 750000,
      location: {
        city: 'Multiple Districts',
        country: 'Uganda',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/7ED321/ffffff?text=Clean+Water',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Water & Sanitation',
      projectCategoriesNames: ['Water', 'Sanitation', 'Community Health', 'Infrastructure', 'Public Health', 'Environmental Conservation'],
      amountRaisedToDate: 450000,
      totalProjectBudget: 750000,
      daysLeft: 520,
      givingLink: 'https://globalhopefoundation.org/donate/clean-water',
      projectLanguages: [
        { code: 'eng', entityStatus: 'LIVE' },
      ],
    },
    // Project 2.5: English + LIVE - FULLY FUNDED (100% funded)
    {
      id: 'project-002-5',
      projectUuid: 'project-002-5',
      name: 'Community Library Construction',
      projectName: 'Community Library Construction',
      description: 'Constructing a modern community library with digital resources and reading spaces for youth and adults.',
      status: 'active',
      startDate: '2023-06-01',
      endDate: '2025-05-31',
      budget: 350000,
      location: {
        city: 'Kampala',
        country: 'Uganda',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/9B59B6/ffffff?text=Library',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Education',
      projectCategoriesNames: ['Education', 'Infrastructure', 'Literacy', 'Community Development', 'Digital Access'],
      amountRaisedToDate: 350000,
      totalProjectBudget: 350000,
      daysLeft: 180,
      givingLink: 'https://globalhopefoundation.org/donate/library',
      projectLanguages: [
        { code: 'eng', entityStatus: 'LIVE' },
      ],
    },
    // Project 3: English + DRAFT (should NOT be visible when viewing in "eng")
    {
      id: 'project-003',
      projectUuid: 'project-003',
      name: 'Healthcare Mobile Clinics',
      projectName: 'Healthcare Mobile Clinics',
      description: 'Deploying mobile healthcare units to provide medical services in remote villages without access to healthcare facilities.',
      status: 'active',
      startDate: '2024-06-01',
      endDate: '2027-05-31',
      budget: 1200000,
      location: {
        city: 'Rural Areas',
        country: 'Tanzania',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/F5A623/ffffff?text=Mobile+Clinics',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Healthcare',
      projectCategoriesNames: ['Healthcare', 'Mobile Services', 'Maternal Health', 'Child Welfare', 'Primary Care', 'Community Health'],
      amountRaisedToDate: 280000,
      totalProjectBudget: 1200000,
      daysLeft: 890,
      givingLink: 'https://globalhopefoundation.org/donate/mobile-clinics',
      projectLanguages: [
        { code: 'eng', entityStatus: 'DRAFT' },
        { code: 'spa', entityStatus: 'LIVE' },
      ],
    },
    // Project 4: Spanish + LIVE only (should NOT be visible when viewing in "eng")
    {
      id: 'project-004',
      projectUuid: 'project-004',
      name: 'Programa de Agricultura Sostenible',
      projectName: 'Programa de Agricultura Sostenible',
      description: 'Sustainable agriculture program training farmers in modern techniques to improve crop yields and income.',
      status: 'active',
      startDate: '2024-02-01',
      endDate: '2026-01-31',
      budget: 450000,
      location: {
        city: 'Guatemala City',
        country: 'Guatemala',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/BD10E0/ffffff?text=Agricultura',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Agriculture',
      projectCategoriesNames: ['Agriculture', 'Training', 'Economic Development', 'Sustainable Farming', 'Food Security', 'Rural Development'],
      amountRaisedToDate: 180000,
      totalProjectBudget: 450000,
      daysLeft: 420,
      givingLink: 'https://globalhopefoundation.org/donate/agriculture',
      projectLanguages: [
        { code: 'spa', entityStatus: 'LIVE' },
      ],
    },
    // Project 5: French + LIVE only (should NOT be visible when viewing in "eng")
    {
      id: 'project-005',
      projectUuid: 'project-005',
      name: 'Programme d\'Autonomisation des Femmes',
      projectName: 'Programme d\'Autonomisation des Femmes',
      description: 'Women empowerment program providing skills training and microfinance support to help women start their own businesses.',
      status: 'active',
      startDate: '2023-09-01',
      endDate: '2025-08-31',
      budget: 300000,
      location: {
        city: 'Dakar',
        country: 'Senegal',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/50E3C2/ffffff?text=Autonomisation',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Women Empowerment',
      projectCategoriesNames: ['Women Empowerment', 'Microfinance', 'Skills Training', 'Entrepreneurship', 'Gender Equality', 'Economic Development'],
      amountRaisedToDate: 210000,
      totalProjectBudget: 300000,
      daysLeft: 280,
      givingLink: 'https://globalhopefoundation.org/donate/women-empowerment',
      projectLanguages: [
        { code: 'fra', entityStatus: 'LIVE' },
      ],
    },
    // Project 6: English + INACTIVE (should NOT be visible when viewing in "eng")
    {
      id: 'project-006',
      projectUuid: 'project-006',
      name: 'Child Nutrition Program',
      projectName: 'Child Nutrition Program',
      description: 'Providing nutritious meals and nutrition education to combat malnutrition in children under five years old.',
      status: 'active',
      startDate: '2024-04-01',
      endDate: '2026-03-31',
      budget: 850000,
      location: {
        city: 'Addis Ababa',
        country: 'Ethiopia',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/B8E986/ffffff?text=Nutrition',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Child Welfare',
      projectCategoriesNames: ['Child Welfare', 'Nutrition', 'Health Education', 'Food Security', 'Early Childhood Development', 'Public Health'],
      amountRaisedToDate: 325000,
      totalProjectBudget: 850000,
      daysLeft: 485,
      givingLink: 'https://globalhopefoundation.org/donate/nutrition',
      projectLanguages: [
        { code: 'eng', entityStatus: 'INACTIVE' },
      ],
    },
    // Project 7: All languages + LIVE (should be visible when viewing in any language)
    {
      id: 'project-007',
      projectUuid: 'project-007',
      name: 'Global Literacy Campaign',
      projectName: 'Global Literacy Campaign',
      description: 'A worldwide initiative to improve literacy rates through innovative teaching methods and community reading programs.',
      status: 'active',
      startDate: '2024-01-01',
      endDate: '2026-12-31',
      budget: 2000000,
      location: {
        city: 'Multiple Countries',
        country: 'Global',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/FF6B6B/ffffff?text=Literacy',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Education',
      projectCategoriesNames: ['Education', 'Literacy', 'Community Development', 'Innovation', 'Teacher Training', 'Adult Education', 'Digital Literacy'],
      amountRaisedToDate: 1350000,
      totalProjectBudget: 2000000,
      daysLeft: 730,
      givingLink: 'https://globalhopefoundation.org/donate/literacy',
      projectLanguages: [
        { code: 'eng', entityStatus: 'LIVE' },
        { code: 'spa', entityStatus: 'LIVE' },
        { code: 'fra', entityStatus: 'LIVE' },
      ],
    },
    // Project 8: English + ARCHIVED (should NOT be visible when viewing in "eng")
    {
      id: 'project-008',
      projectUuid: 'project-008',
      name: 'Emergency Relief Fund',
      projectName: 'Emergency Relief Fund',
      description: 'Rapid response fund for natural disasters and emergencies to provide immediate aid to affected communities.',
      status: 'completed',
      startDate: '2023-01-01',
      endDate: '2023-12-31',
      budget: 500000,
      location: {
        city: 'Various',
        country: 'Global',
      },
      coverImageUrl: 'https://via.placeholder.com/800x600/4ECDC4/ffffff?text=Emergency+Relief',
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Emergency Response',
      projectCategoriesNames: ['Emergency Response', 'Disaster Relief', 'Humanitarian Aid', 'Crisis Management', 'Food Security', 'Shelter'],
      amountRaisedToDate: 500000,
      totalProjectBudget: 500000,
      daysLeft: 0,
      givingLink: 'https://globalhopefoundation.org/donate/emergency',
      projectLanguages: [
        { code: 'eng', entityStatus: 'ARCHIVED' },
      ],
    },
    // Project 9: English + LIVE with EMPTY coverImageUrl (to test default placeholder)
    {
      id: 'project-009',
      projectUuid: 'project-009',
      name: 'Digital Skills Training',
      projectName: 'Digital Skills Training',
      description: 'Providing digital literacy and computer skills training to youth to improve their employability and economic opportunities.',
      status: 'active',
      startDate: '2024-08-01',
      endDate: '2026-07-31',
      budget: 400000,
      location: {
        city: 'Mumbai',
        country: 'India',
      },
      coverImageUrl: '', // Empty string to test default placeholder image
      organisationName: 'Global Hope Foundation',
      projectCauseName: 'Education',
      projectCategoriesNames: ['Education', 'Digital Skills', 'Youth Development', 'Technology', 'Employment'],
      amountRaisedToDate: 120000,
      totalProjectBudget: 400000,
      daysLeft: 650,
      givingLink: 'https://globalhopefoundation.org/donate/digital-skills',
      projectLanguages: [
        { code: 'eng', entityStatus: 'LIVE' },
      ],
    },
  ],

  financials: [
    {
      year: 2024,
      revenue: 15000000,
      expenses: 13500000,
      assets: 8000000,
      liabilities: 2000000,
      currency: 'USD',
    },
    {
      year: 2023,
      revenue: 12000000,
      expenses: 11000000,
      assets: 6500000,
      liabilities: 1500000,
      currency: 'USD',
    },
    {
      year: 2022,
      revenue: 9500000,
      expenses: 8800000,
      assets: 5000000,
      liabilities: 1200000,
      currency: 'USD',
    },
  ],

  financialReports: [
    // 2024 Financial Year (Position 1 - Most Recent)
    {
      year: 2024,
      currency: 'SGD',
      position: 1,
      revenue: {
        items: [
          { description: 'School Income', actual: 465300.00 },
          { description: 'Financial Gifts & donations', actual: 1116500.00 },
          { description: 'Govt Grants & Misc Income', actual: 100000.00 }
        ],
        total: 1681800.00
      },
      expense: {
        items: [
          { description: 'Staff salaries, staff welfare & training & CPF', actual: 646500.00 },
          { description: 'General & OPEX', actual: 575500.00 },
          { description: 'Mission & Ministry Support/ Love Gift', actual: 44500.00 }
        ],
        total: 1266500.00
      },
      netGainLoss: {
        totalRevenue: 1681800.00,
        totalExpense: 1266500.00,
        netAmount: 415300.00,
        type: 'gain' as const
      }
    },
    // 2023 Financial Year (Position 2)
    {
      year: 2023,
      currency: 'SGD',
      position: 2,
      revenue: {
        items: [
          { description: 'School Income', actual: 396800.00 },
          { description: 'Financial Gifts & donations', actual: 796000.00 },
          { description: 'Govt Grants & Misc Income', actual: 98300.00 }
        ],
        total: 1291100.00
      },
      expense: {
        items: [
          { description: 'Staff salaries, staff welfare & training & CPF', actual: 574700.00 },
          { description: 'General & OPEX', actual: 409100.00 },
          { description: 'Mission & Ministry Support/ Love Gift', actual: 61000.00 }
        ],
        total: 1044800.00
      },
      netGainLoss: {
        totalRevenue: 1291100.00,
        totalExpense: 1044800.00,
        netAmount: 246300.00,
        type: 'gain' as const
      }
    },
    // 2022 Financial Year (Position 3)
    {
      year: 2022,
      currency: 'SGD',
      position: 3,
      revenue: {
        items: [
          { description: 'School Income', actual: 387500.00 },
          { description: 'Financial Gifts & donations', actual: 529200.00 },
          { description: 'Govt Grants & Misc Income', actual: 145800.00 }
        ],
        total: 1062500.00
      },
      expense: {
        items: [
          { description: 'Staff salaries, staff welfare & training & CPF', actual: 541200.00 },
          { description: 'General & OPEX', actual: 333300.00 },
          { description: 'Mission & Ministry Support/ Love Gift', actual: 60300.00 }
        ],
        total: 934800.00
      },
      netGainLoss: {
        totalRevenue: 1062500.00,
        totalExpense: 934800.00,
        netAmount: 127700.00,
        type: 'gain' as const
      }
    }
  ],

  documents: [
    {
      id: 'doc-001',
      title: 'Annual Financial Report 2024',
      type: 'PDF',
      url: 'https://globalhopefoundation.org/docs/annual-report-2024.pdf',
      uploadDate: '2024-03-01',
      size: 2500000,
    },
    {
      id: 'doc-002',
      title: 'IRS Form 990 - 2023',
      type: 'PDF',
      url: 'https://globalhopefoundation.org/docs/financial-statements-2023.pdf',
      uploadDate: '2024-02-15',
      size: 1800000,
    },
    {
      id: 'doc-003',
      title: 'Impact Report 2024',
      type: 'PDF',
      url: 'https://globalhopefoundation.org/docs/form-990-2023.pdf',
      uploadDate: '2024-05-15',
      size: 1200000,
    },
    {
      id: 'doc-004',
      title: 'Sustainability Report 2023',
      type: 'PDF',
      url: 'https://globalhopefoundation.org/docs/sustainability-report-2023.pdf',
      uploadDate: '2024-01-20',
      size: 3200000,
    },
    {
      id: 'doc-005',
      title: 'Donor Recognition Report',
      type: 'PDF',
      url: 'https://globalhopefoundation.org/docs/donor-recognition-2023.pdf',
      uploadDate: '2024-04-10',
      size: 950000,
    },
    {
      id: 'doc-006',
      title: 'Comprehensive Annual Strategic Planning and Impact Assessment Report with Detailed Financial Analysis and Community Engagement Metrics for Sustainable Development Programs 2024',
      type: 'PDF',
      url: 'https://globalhopefoundation.org/docs/comprehensive-strategic-planning-report-2024.pdf',
      uploadDate: '2024-06-01',
      size: 4500000,
    },
  ],

  champions: [
    {
      uuid: 'champion-001',
      entityName: 'Global Hope Foundation',
      firstName: 'Robert',
      lastName: 'Anderson',
      email: 'robert.anderson@example.com',
      coverImage: {
        id: 'champion-cover-001',
        url: 'https://via.placeholder.com/1200x400/96080b/ffffff?text=Champion+Story',
        alt: 'Champion Cover Image',
      },
      photo: {
        id: 'champion-photo-001',
        url: 'https://via.placeholder.com/400x400/4A90E2/ffffff?text=RA',
        alt: 'Robert Anderson',
      },
      testimonial: 'Supporting Global Hope Foundation has been one of the most rewarding experiences of my life. Seeing firsthand how my contributions have helped build schools and provide healthcare to communities in need has been truly inspiring. The transparency and impact of this organization are unmatched.',
      yearsOfEngagement: '5 years',
      dueDiligenceLevel: 'Comprehensive',
      grantsAwardedNumber: 12,
      currency: 'USD',
      quote: 'This organization truly transforms lives and communities.',
      history: 'Robert has been a major donor since 2019, funding multiple education and healthcare projects across East Africa.',
      impactStory: 'Through Robert\'s generous support, we have built 15 schools, trained 200 teachers, and provided healthcare services to over 50,000 people.',
      engagementYear: 2019,
      engagementMonth: 6,
      validityYear: 2025,
      validityMonth: 12,
      photoOrVideo: 'photo',
      championTestimonialUuid: 'champion-testimonial-001',
      document: null,
      video: null,
      topRisks: null,
    },
  ],

  createdAt: '2009-01-15T00:00:00Z',
  updatedAt: '2024-11-18T00:00:00Z',
};

/**
 * Mock charity with a very long name (300 characters) to test multi-line name display
 * Use this to verify that lengthy charity names wrap properly without overlapping other components
 */
export const MOCK_CHARITY_LONG_NAME: Charity = {
  ...MOCK_CHARITY_DATA,
  id: 'mock-charity-002',
  uuid: 'mock-charity-002',
  name: 'The International Foundation for Sustainable Community Development, Education, Healthcare, Environmental Conservation, Economic Empowerment, and Social Transformation Through Innovative Partnerships and Collaborative Global Initiatives for a Better Tomorrow and Brighter Future Worldwide Organization',
  details: {
    ...MOCK_CHARITY_DATA.details,
    website: '', // Empty website to test conditional icon hiding
  },
  givingLink: 'https://globalhopefoundation.org/donate',
  address: {
    street: '',
    city: '',
    state: '',
    postalCode: '',
    country: '', // Empty address to test conditional icon hiding
  },
};
